'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function EnIyiDolguMarkasiPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/en-iyi-dolgu-markasi-klinik-guvenlik-ve-kalicilik-rehberi';
  const pageTitle = 'En İyi Dolgu Markası: Klinik Güvenlik ve Kalıcılık Rehberi';
  const pageDescription = 'Dolgu yaptırmadan önce hangi markalar güvenilir? En iyi dolgu markaları, FDA onay durumları ve kullanım alanları nelerdir?';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'En İyi Dolgu Markası', href: '/tr/blog/en-iyi-dolgu-markasi-klinik-guvenlik-ve-kalicilik-rehberi' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="En iyi dolgu markaları ve klinik güvenlik rehberi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-emerald-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Dolgu Markaları
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              En İyi Dolgu Markası: Klinik Güvenlik ve Kalıcılık Rehberi
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>8 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>8 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Piyasada yüzlerce farklı marka dolgu ürünü bulunur, ancak her biri aynı kaliteye sahip değildir. Estetik tıpta kullanılan dolgu ürünlerinin güvenli, onaylı ve uzun ömürlü olması büyük önem taşır.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Bu yazıda en iyi dolgu markaları, kullanım alanları ve seçim kriterlerini inceleyeceğiz.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Dolgu Seçerken Nelere Dikkat Edilmeli?
              </h2>
              <div className="bg-emerald-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-emerald-800 space-y-2">
                  <li>✅ FDA ve CE onayı olmalı</li>
                  <li>✅ Kökeni belli ve orijinal olmalı</li>
                  <li>✅ Uygulama bölgesine uygun yoğunlukta seçilmeli</li>
                  <li>✅ Muayenehanede soğuk zincir korunmalı</li>
                  <li>✅ Ambalaj açılmadan hasta önünde açılmalı</li>
                </ul>
                <p className="text-emerald-800 mt-3 font-medium">
                  💬 Doğru ürün + doğru uygulayıcı = doğal sonuç.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. En Güvenilir ve Etkili Dolgu Markaları (2026 Güncel Liste)
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-6">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Marka
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Menşei
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        İçerik
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Kalıcılık
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Özellik
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Juvederm® (Allergan)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">ABD</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Hyaluronik asit</td>
                      <td className="px-6 py-4 text-sm text-gray-900">12–18 ay</td>
                      <td className="px-6 py-4 text-sm text-gray-900">En yaygın ve FDA onaylı seri</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Restylane® (Galderma)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">İsveç</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Hyaluronik asit</td>
                      <td className="px-6 py-4 text-sm text-gray-900">9–15 ay</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Doğal sonuç ve yumuşak doku uyumu</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Teosyal® (Teoxane)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">İsviçre</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Hyaluronik asit</td>
                      <td className="px-6 py-4 text-sm text-gray-900">12–18 ay</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Cilt elastikiyetini artırır</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Belotero® (Merz)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Almanya</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Hyaluronik asit</td>
                      <td className="px-6 py-4 text-sm text-gray-900">9–12 ay</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Göz altı ve ince çizgiler için ideal</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Radiesse® (Merz)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Almanya</td>
                      <td className="px-6 py-4 text-sm text-gray-900">CaHA</td>
                      <td className="px-6 py-4 text-sm text-gray-900">18–24 ay</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kolojen stimülasyonu sağlar</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Sculptra® (Galderma)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">ABD</td>
                      <td className="px-6 py-4 text-sm text-gray-900">PLLA</td>
                      <td className="px-6 py-4 text-sm text-gray-900">24–36 ay</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Uzun süreli hacim artışı sağlar</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Revanesse® Versa</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kanada</td>
                      <td className="px-6 py-4 text-sm text-gray-900">HA</td>
                      <td className="px-6 py-4 text-sm text-gray-900">12–16 ay</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Şişlik riski düşüktür</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Neauvia®</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Polonya</td>
                      <td className="px-6 py-4 text-sm text-gray-900">HA + PEG</td>
                      <td className="px-6 py-4 text-sm text-gray-900">12–18 ay</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Hidrofilik yapı, sıkı doku etkisi</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <p className="text-blue-800 font-medium">
                  💎 Muayenehaneler genellikle Juvederm, Restylane ve Teosyal serilerini "gold standard" olarak kabul eder.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Bölgeye Göre Önerilen Dolgu Markaları
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-6">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Bölge
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Önerilen Marka
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Dudak</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Juvederm Volbella, Restylane Kysse</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Burun</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Teosyal Ultra Deep, Radiesse</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Göz Altı</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Belotero Balance, Teosyal Redensity II</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Elmacık Kemikleri</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Juvederm Voluma, Sculptra</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Çene / Jawline</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Radiesse, Juvederm Volux</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Ucuz ve Kalitesiz Dolguların Riskleri
              </h2>
              <div className="bg-red-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-red-800 space-y-2">
                  <li>⚠️ Alerjik reaksiyonlar</li>
                  <li>⚠️ Nodül oluşumu</li>
                  <li>⚠️ Enfeksiyon riski</li>
                  <li>⚠️ Asimetri veya doku bozulması</li>
                </ul>
                <p className="text-red-800 mt-3 font-medium">
                  Dolgu yalnızca lisanslı hekim tarafından, orijinal ambalajda uygulanmalıdır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Dolgu Markasını Nasıl Kontrol Edersiniz?
              </h2>
              <div className="bg-purple-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-purple-800 space-y-2">
                  <li>Seri numarası kutuda yazmalıdır</li>
                  <li>Ambalaj üzerinde üretici firma ve son kullanma tarihi bulunmalıdır</li>
                  <li>Ürün üzerinde hologram ve barkod etiketi yer almalıdır</li>
                  <li>Açılan ürün size gösterilmeli ve barkod yapıştırması hasta dosyasına eklenmelidir</li>
                </ul>
              </div>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="En iyi dolgu markaları ve klinik güvenlik rehberi"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. En kalıcı dolgu markası hangisi?</h3>
                  <p className="text-gray-700">Sculptra (PLLA) ve Radiesse (CaHA) en uzun ömürlü dolgulardır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Dudak için hangi marka en uygundur?</h3>
                  <p className="text-gray-700">Juvederm Volbella ve Restylane Kysse doğal sonuç verir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Dolgu markası gerçekten fark eder mi?</h3>
                  <p className="text-gray-700">Evet, kalite doğrudan kalıcılık ve doku uyumunu belirler.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4. Ucuz dolgu neden risklidir?</h3>
                  <p className="text-gray-700">Genellikle sertifikasız, sulandırılmış veya sahte ürünlerdir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>FDA (U.S. Food and Drug Administration) – "Approved Dermal Fillers List"</li>
                <li>Harvard Health Publishing (2024) – "Choosing the Right Filler for You"</li>
                <li>Türk Estetik Tıp Derneği (TETD) – "Dolgu Güvenlik ve Onaylı Ürünler Rehberi"</li>
                <li>ISAPS 2024 Global Report – "Top Dermal Filler Brands Worldwide"</li>
                <li>European CE Registry – "Certified Injectable Fillers Database"</li>
              </ul>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#dolgu</span>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#hyaluronikasit</span>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#estetik</span>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#juvederm</span>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#restylane</span>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#sculptra</span>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#teosyal</span>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#radiesse</span>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Dolgu Markaları Hakkında Bilgi Alın</h3>
              <p className="mb-6">En iyi dolgu markaları ve güvenli uygulama hakkında uzman doktorumuzdan detaylı bilgi alın.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('En iyi dolgu markaları hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-emerald-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/dolgunun-erime-suresi-ne-zaman-ve-neden-azalir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Dolgunun Erime Süresi</h4>
              <p className="text-gray-600 text-sm">Dolgu ne kadar sürede erir? Kalıcılığını etkileyen faktörler nelerdir?</p>
            </Link>
            <Link
              href="/tr/blog/dolgu-islemleri-yuz-liftingi-saglar-mi"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Dolgu İşlemleri Yüz Liftingi Sağlar mı?</h4>
              <p className="text-gray-600 text-sm">Dolgu işlemlerinin yüz liftingi etkisi yaratıp yaratmadığı ve nasıl uygulandığı.</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
